import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { computeSummary } from './budgetCalc';

const emptySummary = computeSummary([], [], 'RON');

const initialState = {
    status: 'idle',          // idle | loading | succeeded | failed
    saveStatus: 'idle',      // idle | saving | saved | error
    error: null,
    exists: false,
    prefilled: false,
    currency: 'RON',
    incomeSources: [],
    expenseCategories: [],
    summary: emptySummary,
    nextKey: 1,
};

export const fetchBudget = createAsyncThunk('budget/fetch', async () => {
    const response = await axios({
        method: 'get',
        url: process.env.NEXT_PUBLIC_API + '/api/user/budget',
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
    });
    return response.data;
});

export const saveBudget = createAsyncThunk('budget/save', async (payload) => {
    const response = await axios({
        method: 'post',
        url: process.env.NEXT_PUBLIC_API + '/api/user/budget',
        data: payload,
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
    });
    return response.data;
});

// Asigura cheie stabila pe fiecare rand (React key + editare locala).
const withKeys = (rows, startKey) => {
    let key = startKey;
    const out = (rows || []).map((r) => ({
        key: key++,
        id: r.id ?? null,
        type: r.type,
        label: r.label ?? '',
        amount: r.amount ?? 0,
    }));
    return { rows: out, nextKey: key };
};

const recompute = (state) => {
    state.summary = computeSummary(state.incomeSources, state.expenseCategories, state.currency);
};

const budgetSlice = createSlice({
    name: 'budget',
    initialState,
    reducers: {
        addIncome: (state, action) => {
            state.incomeSources.push({ key: state.nextKey++, id: null, type: action.payload || 'salary', label: '', amount: 0 });
            recompute(state);
        },
        updateIncome: (state, action) => {
            const { key, field, value } = action.payload;
            const row = state.incomeSources.find((r) => r.key === key);
            if (row) row[field] = field === 'amount' ? value : value;
            recompute(state);
        },
        removeIncome: (state, action) => {
            state.incomeSources = state.incomeSources.filter((r) => r.key !== action.payload);
            recompute(state);
        },
        addExpense: (state, action) => {
            state.expenseCategories.push({ key: state.nextKey++, id: null, type: action.payload || 'housing', label: '', amount: 0 });
            recompute(state);
        },
        updateExpense: (state, action) => {
            const { key, field, value } = action.payload;
            const row = state.expenseCategories.find((r) => r.key === key);
            if (row) row[field] = value;
            recompute(state);
        },
        removeExpense: (state, action) => {
            state.expenseCategories = state.expenseCategories.filter((r) => r.key !== action.payload);
            recompute(state);
        },
        resetSaveStatus: (state) => {
            state.saveStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBudget.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchBudget.fulfilled, (state, action) => {
                const { status, data } = action.payload;
                if (status && data) {
                    const income = withKeys(data.incomeSources, 1);
                    const expense = withKeys(data.expenseCategories, income.nextKey);
                    state.incomeSources = income.rows;
                    state.expenseCategories = expense.rows;
                    state.nextKey = expense.nextKey;
                    state.currency = data.currency || 'RON';
                    state.exists = !!data.exists;
                    state.prefilled = !!data.prefilled;
                    state.summary = data.summary || computeSummary(state.incomeSources, state.expenseCategories, state.currency);
                    state.status = 'succeeded';
                } else {
                    state.status = 'failed';
                }
            })
            .addCase(fetchBudget.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error ? action.error.message : 'error';
            })
            .addCase(saveBudget.pending, (state) => {
                state.saveStatus = 'saving';
            })
            .addCase(saveBudget.fulfilled, (state, action) => {
                const { status, data } = action.payload;
                if (status && data) {
                    state.exists = true;
                    state.prefilled = false;
                    state.summary = data.summary || state.summary;
                    state.saveStatus = 'saved';
                } else {
                    state.saveStatus = 'error';
                    state.error = action.payload.error || 'error';
                }
            })
            .addCase(saveBudget.rejected, (state, action) => {
                state.saveStatus = 'error';
                state.error = action.error ? action.error.message : 'error';
            });
    },
});

export const {
    addIncome, updateIncome, removeIncome,
    addExpense, updateExpense, removeExpense,
    resetSaveStatus,
} = budgetSlice.actions;
export default budgetSlice.reducer;
